<template>
  <div class="view-container">
    <!-- 美化后的搜索栏 -->
    <div class="search-panel-v2">
      <div class="search-bar">
        <div class="search-input-wrap">
          <i class="fas fa-search search-icon"></i>
          <input v-model="searchKeyword" type="text" class="search-input" placeholder="搜索效率报表名称..." @keyup.enter="doSearch">
          <button v-if="searchKeyword" class="search-clear" @click="searchKeyword=''"><i class="fas fa-times"></i></button>
        </div>
        <span class="search-info">共 {{ filteredReports.length }} 个效率报表</span>
        <button v-if="searchKeyword" @click="resetSearch" class="btn btn-reset"><i class="fas fa-undo"></i> 清除</button>
      </div>
    </div>

    <div v-if="loading" class="loading-page"><i class="fas fa-spinner fa-pulse fa-2x"></i><p>正在加载报表...</p></div>

    <div v-else class="report-grid">
      <div v-for="(report, idx) in filteredReports" :key="report.guid || report.Guid || idx" class="report-card-wrapper">
        <div class="report-card" @click="openReport(report)">
          <div class="report-card-header">
            <div class="report-icon" :style="{ background: getBgColor(idx) }"><i :class="getReportIcon(report)"></i></div>
            <div class="report-title-area">
              <h3 class="report-name">{{ report.chName || report.ChName || '未命名报表' }}</h3>
              <p class="report-desc">{{ report.description || report.Description || '' }}</p>
            </div>
            <div class="report-arrow"><i class="fas fa-arrow-right"></i></div>
          </div>
          <div class="report-meta-row">
            <span class="meta-tag"><i class="fas fa-user"></i> {{ report.creater || report.Creater || '-' }}</span>
            <span class="meta-tag"><i class="fas fa-calendar"></i> {{ formatDate(report.createTime || report.CreateTime) }}</span>
            <span class="meta-tag"><i class="fas fa-tag"></i> {{ getChartTypeName(report.enName || report.EnName) }}</span>
          </div>
        </div>
      </div>
      <div v-if="filteredReports.length === 0 && !loading" class="empty-grid"><i class="fas fa-chart-pie"></i><span>暂无效率报表</span></div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { getReports, getCatalogs } from '../api/report'

// ============ 状态 ============
const router = useRouter()
const searchKeyword = ref('')
const reports = ref([])
const loading = ref(false)

// ============ 背景色列表 ============
const bgColors = ['#6366f1', '#10b981', '#f59e0b', '#ec4899', '#ef4444', '#8b5cf6', '#3B82F6', '#14B8A6']

// ============ 辅助函数 ============
const getBgColor = (idx) => bgColors[idx % bgColors.length]

const getReportIcon = (report) => {
  const enName = (report?.enName || report?.EnName || '0').toString()
  const iconMap = { '0': 'fas fa-list', '1': 'fas fa-chart-pie', '2': 'fas fa-chart-bar', '3': 'fas fa-chart-line', '4': 'fas fa-chart-pie', '5': 'fas fa-chart-bar', '6': 'fas fa-chart-line' }
  return iconMap[enName] || 'fas fa-chart-bar'
}

const getChartTypeName = (enName) => {
  const map = { '0': '列表', '1': '饼图', '2': '柱状图', '3': '折线图', '4': '饼图+列表', '5': '柱状图+列表', '6': '折线图+列表' }
  return map[String(enName)] || '未知'
}

const formatDate = (val) => {
  if (!val) return '-'
  const str = String(val)
  return str.length >= 10 ? str.slice(0, 10) : str
}

const getField = (obj, camel, pascal) => (obj && obj[camel] !== undefined) ? obj[camel] : (obj ? obj[pascal] : undefined)

// ============ 过滤报表 ============
const filteredReports = computed(() => {
  let list = reports.value || []
  if (searchKeyword.value) {
    const kw = searchKeyword.value.toLowerCase()
    list = list.filter(r => {
      const name = (getField(r, 'chName', 'ChName') || '').toLowerCase()
      const desc = (getField(r, 'description', 'Description') || '').toLowerCase()
      return name.includes(kw) || desc.includes(kw)
    })
  }
  return list
})

// ============ 搜索 ============
const doSearch = () => { /* computed 自动过滤 */ }
const resetSearch = () => { searchKeyword.value = '' }

// ============ 打开报表 ============
const openReport = (report) => {
  const guid = getField(report, 'guid', 'Guid')
  if (guid) {
    const resolved = router.resolve({ path: '/report-viewer', query: { reportGuid: guid } })
    window.open(resolved.href, '_blank')
  }
}

// ============ 初始化 ============
onMounted(async () => {
  loading.value = true
  try {
    const catRes = await getCatalogs()
    const catalogs = Array.isArray(catRes?.data) ? catRes.data : (Array.isArray(catRes?.Data) ? catRes.Data : [])
    let efficientGuid = ''
    for (const c of catalogs) {
      const name = (c.enName || c.EnName || '').toLowerCase()
      if (name.includes('效率报表') || name.includes('效率')) {
        efficientGuid = c.guid || c.Guid || ''
        break
      }
    }
    console.log('效率报表 catalogGuid found:', efficientGuid || '(not found)')

    if (efficientGuid) {
      const repRes = await getReports(efficientGuid)
      let list = []
      if (repRes && Array.isArray(repRes)) { list = repRes }
      else if (repRes && repRes.data && Array.isArray(repRes.data)) { list = repRes.data }
      else if (repRes && repRes.Data && Array.isArray(repRes.Data)) { list = repRes.Data }
      console.log('SystemReports loaded:', list.length, 'reports')
      reports.value = list
    } else {
      console.warn('未找到"效率报表"目录，请检查 SYSCommonCATALOG 表中是否存在')
      reports.value = []
    }
  } catch (e) { console.error('加载效率报表失败', e) }
  finally { loading.value = false }
})
</script>

<style scoped>
.view-container { padding: 20px; background: #fff; border-radius: 8px; box-shadow: 0 2px 8px rgba(0,0,0,0.05); display: flex; flex-direction: column; max-height: calc(100vh - 150px); overflow: hidden; margin: 16px; }

/* ============ 美化搜索栏 ============ */
.search-panel-v2 { flex-shrink: 0; margin-bottom: 16px; }
.search-bar { display: flex; align-items: center; gap: 12px; padding: 10px 16px; background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 12px; flex-wrap: wrap; }
.search-input-wrap { position: relative; flex: 1; min-width: 200px; max-width: 360px; }
.search-icon { position: absolute; left: 12px; top: 50%; transform: translateY(-50%); color: #94a3b8; font-size: 14px; pointer-events: none; z-index: 1; }
.search-input { width: 100%; padding: 9px 36px 9px 36px; border: 1px solid #e2e8f0; border-radius: 8px; font-size: 13px; color: #334155; background: #fff; outline: none; transition: all 0.2s; box-sizing: border-box; }
.search-input:focus { border-color: #6366f1; box-shadow: 0 0 0 3px rgba(99,102,241,0.1); }
.search-clear { position: absolute; right: 6px; top: 50%; transform: translateY(-50%); background: none; border: none; color: #94a3b8; cursor: pointer; padding: 4px 6px; font-size: 13px; border-radius: 50%; transition: all 0.15s; }
.search-clear:hover { color: #ef4444; background: #fee2e2; }
.search-info { font-size: 12px; color: #94a3b8; white-space: nowrap; margin-left: 4px; }
.btn-reset { display: inline-flex; align-items: center; gap: 4px; padding: 8px 14px; background: #fff; border: 1px solid #e2e8f0; border-radius: 8px; font-size: 12px; color: #64748b; cursor: pointer; transition: all 0.2s; }
.btn-reset:hover { background: #f1f5f9; color: #6366f1; border-color: #6366f1; }

.loading-page { display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 80px; color: #999; gap: 12px; flex: 1; }

.report-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(320px, 1fr)); gap: 16px; overflow-y: auto; flex: 1; padding: 2px; }
.report-card-wrapper { min-width: 0; }
.report-card { background: #fff; border: 1px solid #e7eaec; border-radius: 10px; overflow: hidden; cursor: pointer; transition: all 0.25s ease; }
.report-card:hover { border-color: #10b981; box-shadow: 0 4px 20px rgba(16,185,129,0.12); transform: translateY(-2px); }
.report-card-header { display: flex; align-items: center; gap: 14px; padding: 16px 18px; }
.report-icon { width: 50px; height: 50px; border-radius: 12px; display: flex; align-items: center; justify-content: center; color: #fff; font-size: 22px; flex-shrink: 0; }
.report-title-area { flex: 1; min-width: 0; }
.report-name { font-size: 15px; font-weight: 600; color: #333; margin: 0 0 4px 0; }
.report-desc { font-size: 12px; color: #888; margin: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.report-arrow { color: #ccc; font-size: 16px; transition: all 0.2s; }
.report-card:hover .report-arrow { color: #10b981; }
.report-meta-row { display: flex; gap: 12px; padding: 0 18px 14px; flex-wrap: wrap; }
.meta-tag { font-size: 12px; color: #999; display: inline-flex; align-items: center; gap: 4px; }

.empty-grid { grid-column: 1/-1; text-align: center; padding: 60px; color: #999; }
.empty-grid i { font-size: 48px; margin-bottom: 10px; display: block; }
</style>
